import {ConnectContactSmsEvent, ConnectContactSmsEventRole} from '@/api/dto/connect';
import {User} from '@/api/dto/dto';
import Avatar from '@common/Avatar';
import AvatarDetails from '@common/AvatarDetails';
import {useAuthStore} from '@/store/auth_store';
import ExpandMoreButton from '@common/timeline/ExpandMoreButton';
import DisplayHelpers from '@/helpers/display_helpers';
import {SmsEvent, SmsEventParticipant, SmsEventContent, SmsEventSender}
  from '@/components/pages/tasks/aws_communications/contact/AwsCommunicationsContactSmsEvent.styles';

type Props = {
  event: ConnectContactSmsEvent;
}

const PREVIEW_LENGTH = 120;

// Build a profile for the sender hover card. Prefer the full current-user object; otherwise, for any
// other known sender, assemble a partial user from the backend-enriched fields. The popover renders
// "first_name last_name", so the full senderName is placed in first_name (no fragile name split).
// Returns undefined for an unknown sender (patient / unassociated), so the UI falls back to a plain
// icon. See CARECO-6077.
export function resolveSenderUser(
  event: ConnectContactSmsEvent,
  currentUser?: User | null,
): Partial<User> | undefined {
  if (currentUser && event.senderId && event.senderId === currentUser.id) {
    return currentUser;
  }
  if (event.senderId && event.senderName) {
    return {
      id: event.senderId,
      first_name: event.senderName,
      last_name: '',
      email: event.senderEmail ?? '',
      image: event.senderImage ?? '',
    };
  }
  return undefined;
}

export default function AwsCommunicationsContactSmsEvent({ event }: Props) {
  const { currentUser } = useAuthStore();
  const own = event.role !== ConnectContactSmsEventRole.CUSTOMER;

  // Attribution (matches the conversation modal): show the sender's name when we know the user.
  // When the message is from the current user, render their actual careco avatar. Only fall back
  // to "Bot" when there is genuinely no associated user (an automated/event-driven send).
  const isCurrentUser = own && !!event.senderId && event.senderId === currentUser?.id;
  const senderLabel = own
    ? (event.senderName || (event.role === ConnectContactSmsEventRole.BOT ? 'Bot' : 'Agent'))
    : undefined;

  // Any known sender (resolved to a careco user by the backend, carrying senderId) gets the shared
  // profile hover card. Prefer the full current-user object; otherwise build a partial user from the
  // enriched sender fields (name/email/avatar). Unknown senders fall back to a plain icon.
  const senderUser = resolveSenderUser(event, isCurrentUser ? currentUser : undefined);

  const content = event.content ?? '';

  return (
    <SmsEvent own={own}>
      <SmsEventParticipant>
        {senderUser
          ? <AvatarDetails user={senderUser} />
          : <Avatar user={undefined} />}
      </SmsEventParticipant>
      <SmsEventContent>
        {own && senderLabel && <SmsEventSender>{senderLabel}</SmsEventSender>}
        {DisplayHelpers.formatDateTimeShort(event.timestamp)}
        <span>
          {content.slice(0, PREVIEW_LENGTH)}
          {content.length > PREVIEW_LENGTH && (
            <ExpandMoreButton
              buttonText={(isOpen) => (isOpen ? 'show less' : 'show more')}
              expandItems={[content.slice(PREVIEW_LENGTH)]}
              useExpandHideText={false}
              active={false}
              inline
            />
          )}
        </span>
      </SmsEventContent>
    </SmsEvent>
  );
}
